import { useState, useMemo, useEffect, useRef, type ChangeEvent } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useChecklistById } from "@/hooks/useChecklists";
import { useInventoryForCleaner } from "@/hooks/useCleaner";
import { useProofStandardsForChecklist, type ProofStandard } from "@/hooks/useProofStandards";
import { toast } from "@/hooks/use-toast";
import {
  parseAccessVerificationSettings,
  parseProofMediaByStandardIdFromEntryState,
  parseFunctionalPassStandardIdsFromEntryState,
  type ChecklistEntryType,
  DEFAULT_ACCESS_VERIFICATION_SETTINGS,
} from "@/types/lifecycle";
import type { Room } from "@/types/checklist";
import { sendCleanerIncidentEmail } from "@/lib/notificationEmails";
import {
  Camera,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  X,
  Loader2,
  KeyRound,
  Info,
  ImageIcon,
  Shield,
  Plus,
  Video,
  ClipboardCheck,
} from "lucide-react";
import { AccessInstructionsView } from "@/components/owner/AccessInstructionsView";
import type { PropertyAccessInstructions } from "@/hooks/useProperties";
import type { StartLocationSnapshot } from "@/hooks/useCurrentCleaningTask";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  CLEANER_MOBILE_BOTTOM_NAV_OFFSET,
  CLEANER_MOBILE_EMBED_FOOTER_SCROLL_PAD,
} from "@/lib/turnoverChrome";
import { UnitCheckRoomWalkthrough } from "@/components/cleaner/UnitCheckRoomWalkthrough";
import {
  isProofStandardFullySatisfied,
  isUnitCheckSatisfied,
  proofUrlsForUnitCheck,
  unitCheckProofKey,
} from "@/lib/unitCheckProofKeys";
import { buildUnitCheckWalkthrough } from "@/lib/proofStandardsDisplay";
import { ProofStandardHostDetailContent, UnitItemProofTypeBadge } from "@/components/cleaner/ProofStandardHostDetailContent";
import { UnitCheckIssueReport } from "@/components/cleaner/UnitCheckIssueReport";
import { CleanerMediaLightbox } from "@/components/cleaner/CleanerMediaLightbox";

type ConditionPhotoSlot = { file: File; previewUrl: string; uploadedUrl?: string };

interface EntryWalkthroughStepProps {
  propertyId: string;
  taskId: string;
  checklistId: string | null;
  cleanerId: string;
  onCompleted: () => void;
  tDynamic: (s: string) => string;
  /** Default full flow; access = keys + entry only; proof = standards + condition + finish (optionally with timer). */
  sectionMode?: "all" | "access" | "proof";
  /** Property lockbox / entry copy (same shape as owner settings). Shown first in access or full mode when set. */
  accessInstructions?: PropertyAccessInstructions | null;
  /** Merged task.entry_walkthrough_state for proof mode (access saved in a prior step). */
  existingEntryWalkthroughState?: Record<string, unknown> | null;
  /** GPS snapshot from the confirm-location step; required when onStartVisitTimer is set. */
  pendingStartLocation?: StartLocationSnapshot | null;
  /** After saving entry state, start the visit timer (pre-start flow). */
  onStartVisitTimer?: (args: { extraDirty: boolean; extraDirtyNotes: string | null }) => Promise<void>;
  /** After access-only save succeeds. */
  onAccessPhaseComplete?: () => void;
  /** Hide the in-card title block when the parent shows the pre-visit step heading. */
  omitEmbeddedCardHeader?: boolean;
  /** Turnover pre-visit Step 1 mobile UI (parent sets data-previsit-ui for tokens). */
  turnoverPrevisitMobileUi?: boolean;
  /** Proof step: address line for “timer starts at …” banner (e.g. formatted property address). */
  preVisitTimerAddressLabel?: string | null;
}

export function EntryWalkthroughStep({
  propertyId,
  taskId,
  checklistId,
  cleanerId,
  onCompleted,
  tDynamic,
  sectionMode = "all",
  accessInstructions = null,
  existingEntryWalkthroughState = null,
  pendingStartLocation = null,
  onStartVisitTimer,
  onAccessPhaseComplete,
  omitEmbeddedCardHeader = false,
  turnoverPrevisitMobileUi = false,
  preVisitTimerAddressLabel = null,
}: EntryWalkthroughStepProps) {
  const { t } = useTranslation();
  const { data: checklist } = useChecklistById(checklistId ?? undefined);
  const { data: proofStandardsList = [] } = useProofStandardsForChecklist(checklistId ?? undefined);
  const { data: invItems = [] } = useInventoryForCleaner(propertyId);
  const settings = useMemo(
    () => parseAccessVerificationSettings(checklist?.access_verification_settings),
    [checklist?.access_verification_settings]
  );

  const roomNames = useMemo(() => {
    try {
      const rooms = (checklist?.rooms as Room[]) || [];
      return rooms.map((r) => r.name).filter(Boolean);
    } catch {
      return [];
    }
  }, [checklist?.rooms]);

  const useStandardsForCondition = proofStandardsList.length > 0;

  const unitItems = useMemo(
    () => invItems.filter((i: { inventory_scope?: string | null }) => i.inventory_scope === "unit"),
    [invItems]
  );

  const [entryType, setEntryType] = useState<ChecklistEntryType>(
    settings.default_entry_type ?? DEFAULT_ACCESS_VERIFICATION_SETTINGS.default_entry_type!
  );
  /** Turnover v1: user must pick entry method (Select shows placeholder until confirmed). */
  const [entryConfirmed, setEntryConfirmed] = useState(
    () => !(turnoverPrevisitMobileUi && omitEmbeddedCardHeader && sectionMode === "access")
  );
  const [backupFiles, setBackupFiles] = useState<File[]>([]);
  const [primaryKeyFiles, setPrimaryKeyFiles] = useState<File[]>([]);
  /** Immediate uploads (Turnover v1 access step). */
  const [primaryKeyProofUrls, setPrimaryKeyProofUrls] = useState<string[]>([]);
  const [backupKeyProofUrls, setBackupKeyProofUrls] = useState<string[]>([]);
  const [primaryKeyUploading, setPrimaryKeyUploading] = useState(false);
  const [backupKeyUploading, setBackupKeyUploading] = useState(false);
  const primaryFileInputRef = useRef<HTMLInputElement>(null);
  const backupFileInputRef = useRef<HTMLInputElement>(null);
  const [conditionByRoom, setConditionByRoom] = useState<Record<string, ConditionPhotoSlot[]>>({});
  const conditionByRoomRef = useRef(conditionByRoom);
  conditionByRoomRef.current = conditionByRoom;
  useEffect(() => {
    return () => {
      Object.values(conditionByRoomRef.current).forEach((slots) => {
        slots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      });
    };
  }, []);
  /** Proof media uploaded immediately (public URLs); continue only persists task row. */
  const [proofMediaUrlsByStandardId, setProofMediaUrlsByStandardId] = useState<Record<string, string[]>>({});
  const [proofUploadingStandardId, setProofUploadingStandardId] = useState<string | null>(null);
  const [proofReferenceModal, setProofReferenceModal] = useState<ProofStandard | null>(null);
  const [keyReferenceViewer, setKeyReferenceViewer] = useState<{ index: number } | null>(null);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [incidentTitle, setIncidentTitle] = useState("");
  const [incidentDesc, setIncidentDesc] = useState("");
  const [incidentFile, setIncidentFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [extraDirty, setExtraDirty] = useState<"no" | "yes">("no");
  const [extraDirtyNotes, setExtraDirtyNotes] = useState("");
  /** functional_check standards marked "pass" without media. */
  const [functionalPassByStandardId, setFunctionalPassByStandardId] = useState<Record<string, boolean>>({});

  const requiresOwnKeyConfirmation = !!settings.cleaner_has_own_key;
  const [ownKeyConfirmed, setOwnKeyConfirmed] = useState(false);

  const existingWalkthroughRef = useRef(existingEntryWalkthroughState);
  existingWalkthroughRef.current = existingEntryWalkthroughState;

  const entryWalkthroughHydrateSigRef = useRef("");

  useEffect(() => {
    entryWalkthroughHydrateSigRef.current = "";
  }, [taskId]);

  useEffect(() => {
    const access = (existingEntryWalkthroughState?.access as Record<string, unknown> | undefined) ?? {};
    if (access.own_key_confirmed === true) {
      setOwnKeyConfirmed(true);
    } else if (!requiresOwnKeyConfirmation) {
      setOwnKeyConfirmed(true);
    } else {
      setOwnKeyConfirmed(false);
    }
  }, [taskId, requiresOwnKeyConfirmation, existingEntryWalkthroughState]);

  useEffect(() => {
    const proof = parseProofMediaByStandardIdFromEntryState(existingEntryWalkthroughState);
    const pass = parseFunctionalPassStandardIdsFromEntryState(existingEntryWalkthroughState);
    const sig = JSON.stringify({ taskId, proof, pass });
    if (entryWalkthroughHydrateSigRef.current === sig) return;
    entryWalkthroughHydrateSigRef.current = sig;
    setProofMediaUrlsByStandardId(proof);
    const next: Record<string, boolean> = {};
    pass.forEach((id) => {
      next[id] = true;
    });
    setFunctionalPassByStandardId(next);
  }, [taskId, existingEntryWalkthroughState]);

  useEffect(() => {
    if (sectionMode !== "proof") return;
    const timer = window.setTimeout(async () => {
      try {
        const prev = (existingWalkthroughRef.current as Record<string, unknown> | null) ?? {};
        const passIds = Object.entries(functionalPassByStandardId)
          .filter(([, v]) => v)
          .map(([k]) => k);
        const prevPre = (prev.pre_visit as Record<string, unknown> | null) ?? {};
        const entry_walkthrough_state: Record<string, unknown> = {
          ...prev,
          proof_media_by_standard_id: proofMediaUrlsByStandardId,
          functional_check_pass_standard_ids: passIds,
          pre_visit: {
            ...prevPre,
            proof_draft_saved_at: new Date().toISOString(),
          },
        };
        const { error } = await supabase
          .from("cleaning_tasks")
          .update({ entry_walkthrough_state })
          .eq("id", taskId)
          .eq("property_id", propertyId);
        if (error) throw error;
      } catch (e) {
        console.error("[EntryWalkthroughStep] proof draft persist:", e);
      }
    }, 900);
    return () => clearTimeout(timer);
  }, [sectionMode, taskId, propertyId, proofMediaUrlsByStandardId, functionalPassByStandardId]);

  const addConditionPhotos = (room: string, files: FileList | null) => {
    if (!files?.length) return;
    const additions: ConditionPhotoSlot[] = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    const startIndex = (conditionByRoomRef.current[room] || []).length;
    setConditionByRoom((prev) => ({
      ...prev,
      [room]: [...(prev[room] || []), ...additions],
    }));
    // Upload immediately in the background — store URL back into slot when done
    const prefix = `condition_${room.replace(/\W+/g, "_")}`;
    additions.forEach((slot, relIdx) => {
      const slotIndex = startIndex + relIdx;
      uploadFiles(prefix, [slot.file]).then((urls) => {
        if (!urls[0]) return;
        setConditionByRoom((prev) => {
          const list = [...(prev[room] || [])];
          if (list[slotIndex]) list[slotIndex] = { ...list[slotIndex], uploadedUrl: urls[0] };
          return { ...prev, [room]: list };
        });
      }).catch(console.error);
    });
  };

  const removeConditionPhoto = (room: string, index: number) => {
    setConditionByRoom((prev) => {
      const list = [...(prev[room] || [])];
      const removed = list[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      if (!removed) return prev;
      list.splice(index, 1);
      const next = { ...prev };
      if (list.length === 0) delete next[room];
      else next[room] = list;
      return next;
    });
  };

  const uploadFiles = async (prefix: string, files: File[]): Promise<string[]> => {
    const results = await Promise.all(
      files.map(async (file, i) => {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `entry_walkthrough/${taskId}/${prefix}_${Date.now()}_${i}.${ext}`;
        const { error } = await supabase.storage.from("cleaning-photos").upload(path, file, {
          contentType: file.type || `image/${ext === "jpg" ? "jpeg" : ext}`,
          upsert: true,
        });
        if (error) { console.error(error); return null; }
        const { data: pub } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
        return pub.publicUrl;
      })
    );
    return results.filter((u): u is string => u !== null);
  };

  const uploadStandardProofFiles = async (standardId: string, files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 40);
      const path = `standards_proof/${propertyId}/${taskId}/${standardId}/${Date.now()}_${i}_${safeName}`;
      const { error } = await supabase.storage.from("cleaning-photos").upload(path, file, {
        contentType: file.type || `image/${ext === "jpg" ? "jpeg" : ext}`,
        upsert: true,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
      urls.push(data.publicUrl);
    }
    return urls;
  };

  const isVideoUrl = (url: string) => /\.(mp4|webm|mov)(\?|$)/i.test(url);

  const handleProofFilesChosen = async (
    standardId: string,
    files: FileList | null,
    roomName?: string,
  ) => {
    if (!files?.length) return;
    const list = Array.from(files);
    const proofKey = roomName ? unitCheckProofKey(standardId, roomName) : standardId;
    setProofUploadingStandardId(proofKey);
    try {
      const urls = await uploadStandardProofFiles(standardId, list);
      if (urls.length === 0) {
        toast({ title: tDynamic("Upload failed"), variant: "destructive" });
        return;
      }
      setProofMediaUrlsByStandardId((prev) => ({
        ...prev,
        [proofKey]: [...(prev[proofKey] ?? []), ...urls],
      }));
      setFunctionalPassByStandardId((prev) => ({ ...prev, [proofKey]: false, [standardId]: false }));
    } catch (e) {
      console.error(e);
      toast({
        title: tDynamic("Upload failed"),
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setProofUploadingStandardId(null);
    }
  };

  const removeProofUrl = (standardId: string, url: string, roomName?: string) => {
    const proofKey = roomName ? unitCheckProofKey(standardId, roomName) : standardId;
    setProofMediaUrlsByStandardId((prev) => {
      const next = { ...prev };
      const rest = (next[proofKey] ?? []).filter((u) => u !== url);
      if (rest.length) next[proofKey] = rest;
      else delete next[proofKey];
      return next;
    });
  };

  const handleSubmitIncident = async () => {
    if (!incidentDesc.trim()) {
      toast({ title: tDynamic("Describe the issue"), variant: "destructive" });
      return;
    }
    let photoUrl: string | null = null;
    if (incidentFile) {
      const urls = await uploadFiles("incident", [incidentFile]);
      photoUrl = urls[0] ?? null;
    }
    const { data: prop } = await supabase.from("properties").select("owner_id, name").eq("id", propertyId).single();
    if (prop?.owner_id) {
      await supabase.from("owner_notifications").insert({
        owner_id: prop.owner_id,
        property_id: propertyId,
        cleaning_task_id: taskId,
        type: "cleaner_incident",
        title: incidentTitle.trim() || tDynamic("Cleaner incident"),
        message: incidentDesc.trim(),
      });
    }
    await sendCleanerIncidentEmail({
      propertyId,
      title: incidentTitle.trim() || "Cleaner incident",
      message: incidentDesc.trim(),
      photoUrl,
    });
    toast({ title: tDynamic("Report sent"), description: tDynamic("The owner has been notified.") });
    setIncidentOpen(false);
    setIncidentTitle("");
    setIncidentDesc("");
    setIncidentFile(null);
  };

  const keyReferenceUrls = settings.key_location_reference_urls ?? [];
  const accessMinimalLayout = omitEmbeddedCardHeader && sectionMode === "access";
  const proofMinimalLayout = omitEmbeddedCardHeader && sectionMode === "proof";
  const turnoverV1 = turnoverPrevisitMobileUi && accessMinimalLayout;
  const proofV1 = Boolean(turnoverPrevisitMobileUi && proofMinimalLayout);
  const proofStickyFooter = proofV1 && Boolean(onStartVisitTimer);
  const embedV1Footer = turnoverV1 || proofStickyFooter;

  useEffect(() => {
    if (turnoverV1) {
      setEntryConfirmed(false);
    }
    setPrimaryKeyProofUrls([]);
    setBackupKeyProofUrls([]);
    setPrimaryKeyFiles([]);
    setBackupFiles([]);
  }, [taskId, turnoverV1]);

  const showKeysSection =
    sectionMode === "access" || (sectionMode === "all" && keyReferenceUrls.length > 0);
  const showPrimaryKeyUpload =
    turnoverV1 ||
    (sectionMode === "access" && keyReferenceUrls.length > 0) ||
    (sectionMode === "all" && keyReferenceUrls.length > 0);

  const handlePrimaryKeyFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const list = Array.from(input.files ?? []);
    input.value = "";
    if (!list.length) return;
    setPrimaryKeyUploading(true);
    try {
      const urls = await uploadFiles("primary_key", list);
      if (!urls.length) {
        toast({ title: tDynamic("Upload failed"), variant: "destructive" });
        return;
      }
      setPrimaryKeyProofUrls((prev) => [...prev, ...urls]);
    } finally {
      setPrimaryKeyUploading(false);
    }
  };

  const handleBackupKeyFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const list = Array.from(input.files ?? []);
    input.value = "";
    if (!list.length) return;
    setBackupKeyUploading(true);
    try {
      const urls = await uploadFiles("backup_key", list);
      if (!urls.length) {
        toast({ title: tDynamic("Upload failed"), variant: "destructive" });
        return;
      }
      setBackupKeyProofUrls((prev) => [...prev, ...urls]);
    } finally {
      setBackupKeyUploading(false);
    }
  };

  const appendOwnKeyToAccess = (access: Record<string, unknown>) => {
    if (requiresOwnKeyConfirmation && ownKeyConfirmed) {
      access.own_key_confirmed = true;
    }
    return access;
  };

  const handleSaveAccessOnly = async () => {
    const keyRefs = keyReferenceUrls;
    if (requiresOwnKeyConfirmation && !ownKeyConfirmed) {
      toast({ title: t("ownKeyConfirmRequired"), variant: "destructive" });
      return;
    }
    if (turnoverV1 && !entryConfirmed) {
      toast({ title: t("keyLocationChooseEntryError"), variant: "destructive" });
      return;
    }
    if (keyRefs.length > 0 && primaryKeyProofUrls.length === 0 && primaryKeyFiles.length === 0) {
      toast({ title: t("keyLocationRequiredPhoto"), variant: "destructive" });
      return;
    }

    if (
      settings.backup_key_proof_required &&
      settings.backup_key_required &&
      backupKeyProofUrls.length === 0 &&
      backupFiles.length === 0
    ) {
      toast({ title: tDynamic("Backup key proof required"), variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const backupUrls = backupKeyProofUrls;
      const primaryUrls = primaryKeyProofUrls;
      const access: Record<string, unknown> = { entry_type: entryType, backup_key_proof_urls: backupUrls };
      if (primaryUrls.length > 0) {
        access.primary_key_proof_urls = primaryUrls;
      }
      appendOwnKeyToAccess(access);
      const prev = (existingEntryWalkthroughState as Record<string, unknown> | null) ?? {};
      const prevPre = (prev.pre_visit as Record<string, unknown> | null) ?? {};
      const entry_walkthrough_state: Record<string, unknown> = {
        ...prev,
        pre_visit: {
          ...prevPre,
          access_saved_at: new Date().toISOString(),
        },
        access,
      };

      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ entry_walkthrough_state })
        .eq("id", taskId)
        .eq("property_id", propertyId);
      if (error) throw error;
      toast({ title: tDynamic("Saved") });
      onAccessPhaseComplete?.();
    } catch (e) {
      console.error(e);
      toast({ title: tDynamic("Error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (sectionMode === "access") return;

    if (sectionMode === "all" && requiresOwnKeyConfirmation && !ownKeyConfirmed) {
      toast({ title: t("ownKeyConfirmRequired"), variant: "destructive" });
      return;
    }

    const skipBackupKeyField =
      sectionMode === "proof" &&
      !!(existingEntryWalkthroughState as { access?: { entry_type?: string } } | null)?.access?.entry_type;

    if (
      !skipBackupKeyField &&
      settings.backup_key_proof_required &&
      settings.backup_key_required &&
      backupKeyProofUrls.length === 0 &&
      backupFiles.length === 0
    ) {
      toast({ title: tDynamic("Backup key proof required"), variant: "destructive" });
      return;
    }

    if (useStandardsForCondition) {
      const required = proofStandardsList.filter((s) => s.required_every_turnover);
      for (const s of required) {
        const ok = isProofStandardFullySatisfied(
          s,
          roomNames,
          proofMediaUrlsByStandardId,
          functionalPassByStandardId,
          tDynamic("General"),
        );
        if (!ok) {
          toast({
            title: tDynamic("Proof required"),
            description: tDynamic(`Upload proof or mark pass for: ${s.title}`),
            variant: "destructive",
          });
          return;
        }
      }
    }

    if (proofUploadingStandardId) {
      toast({ title: tDynamic("Please wait for uploads to finish"), variant: "destructive" });
      return;
    }

    const keyRefs = keyReferenceUrls;
    if (sectionMode === "all" && keyRefs.length > 0 && primaryKeyProofUrls.length === 0 && primaryKeyFiles.length === 0) {
      toast({ title: t("keyLocationRequiredPhoto"), variant: "destructive" });
      return;
    }

    if (onStartVisitTimer && !pendingStartLocation) {
      toast({
        title: tDynamic("Error"),
        description: tDynamic("Confirm your location first."),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      let access: Record<string, unknown>;
      if (sectionMode === "proof") {
        const saved = (existingEntryWalkthroughState?.access as Record<string, unknown> | undefined) ?? {};
        if (!saved.entry_type) {
          toast({
            title: tDynamic("Error"),
            description: tDynamic("Complete keys and access first."),
            variant: "destructive",
          });
          setSubmitting(false);
          return;
        }
        access = saved;
      } else {
        const backupUrls = backupKeyProofUrls;
        const primaryUrls = primaryKeyProofUrls;
        access = { entry_type: entryType, backup_key_proof_urls: backupUrls };
        if (primaryUrls.length > 0) {
          (access as Record<string, unknown>).primary_key_proof_urls = primaryUrls;
        }
        appendOwnKeyToAccess(access as Record<string, unknown>);
      }

      let conditionPhotos: Record<string, string[]> = {};
      let proofMediaByStandardId: Record<string, string[]> = {};

      if (useStandardsForCondition) {
        proofMediaByStandardId = { ...proofMediaUrlsByStandardId };
      }
      // Condition photos are uploaded eagerly on selection; only upload any
      // stragglers that didn't finish yet (edge case: very slow connection).
      for (const room of roomNames) {
        const slots = conditionByRoom[room] || [];
        if (!slots.length) continue;
        const alreadyUploaded = slots.filter((s) => s.uploadedUrl).map((s) => s.uploadedUrl!);
        const pending = slots.filter((s) => !s.uploadedUrl).map((s) => s.file);
        const freshUrls = pending.length
          ? await uploadFiles(`condition_${room.replace(/\W+/g, "_")}`, pending)
          : [];
        conditionPhotos[room] = [...alreadyUploaded, ...freshUrls];
      }

      const prev = (existingEntryWalkthroughState as Record<string, unknown> | null) ?? {};
      const entry_walkthrough_state: Record<string, unknown> = {
        ...prev,
        access,
        completed_at: new Date().toISOString(),
      };
      if (Object.keys(conditionPhotos).length > 0) {
        entry_walkthrough_state.condition_photos_by_room = conditionPhotos;
      }
      if (Object.keys(proofMediaByStandardId).length > 0) {
        entry_walkthrough_state.proof_media_by_standard_id = proofMediaByStandardId;
      }
      const passIds = Object.entries(functionalPassByStandardId)
        .filter(([, v]) => v)
        .map(([k]) => k);
      entry_walkthrough_state.functional_check_pass_standard_ids = passIds;

      const { error } = await supabase
        .from("cleaning_tasks")
        .update({
          entry_walkthrough_completed: true,
          entry_walkthrough_state,
        })
        .eq("id", taskId)
        .eq("property_id", propertyId);
      if (error) throw error;

      if (onStartVisitTimer) {
        await onStartVisitTimer({
          extraDirty: extraDirty === "yes",
          extraDirtyNotes: extraDirty === "yes" ? extraDirtyNotes.trim() || null : null,
        });
      }

      toast({ title: tDynamic("Entry walkthrough saved") });
      onCompleted();
    } catch (e) {
      console.error(e);
      toast({ title: tDynamic("Error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const proofRequiredSatisfied = useMemo(() => {
    if (!useStandardsForCondition) return true;
    const required = proofStandardsList.filter((s) => s.required_every_turnover);
    const general = tDynamic("General");
    return required.every((s) =>
      isProofStandardFullySatisfied(
        s,
        roomNames,
        proofMediaUrlsByStandardId,
        functionalPassByStandardId,
        general,
      ),
    );
  }, [
    useStandardsForCondition,
    proofStandardsList,
    proofMediaUrlsByStandardId,
    functionalPassByStandardId,
    roomNames,
    tDynamic,
  ]);

  const ownKeyGateSatisfied = !requiresOwnKeyConfirmation || ownKeyConfirmed;
  const primaryPhotoRequired = keyReferenceUrls.length > 0 && !requiresOwnKeyConfirmation;
  const accessContinueDisabled = turnoverV1
    ? submitting ||
      proofUploadingStandardId !== null ||
      primaryKeyUploading ||
      backupKeyUploading ||
      !ownKeyGateSatisfied ||
      (primaryPhotoRequired && primaryKeyProofUrls.length === 0 && primaryKeyFiles.length === 0) ||
      !entryConfirmed ||
      (settings.backup_key_proof_required &&
        settings.backup_key_required &&
        backupKeyProofUrls.length === 0 &&
        backupFiles.length === 0)
    : submitting ||
      proofUploadingStandardId !== null ||
      (sectionMode === "access" &&
        (!ownKeyGateSatisfied ||
          (primaryPhotoRequired && primaryKeyProofUrls.length === 0 && primaryKeyFiles.length === 0) ||
          (settings.backup_key_proof_required &&
            settings.backup_key_required &&
            backupKeyProofUrls.length === 0 &&
            backupFiles.length === 0)));

  const showV1PhotoValidation =
    turnoverV1 && primaryPhotoRequired && primaryKeyProofUrls.length === 0 && primaryKeyFiles.length === 0;

  const proofContinueDisabled =
    proofStickyFooter &&
    (submitting ||
      proofUploadingStandardId !== null ||
      !proofRequiredSatisfied ||
      !pendingStartLocation);

  const showProofV1ValidationHint =
    proofStickyFooter && useStandardsForCondition && !proofRequiredSatisfied;

  return (
    <div className={cn("space-y-4", turnoverV1 || proofV1 ? "max-w-md mx-auto w-full" : "max-w-2xl mx-auto")}>
      <Card
        className={cn(
          accessMinimalLayout && !turnoverV1 && !proofV1 && "border-muted/80 shadow-sm",
          (turnoverV1 || proofV1) && "border-0 bg-transparent shadow-none"
        )}
      >
        {!omitEmbeddedCardHeader && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            {sectionMode === "access"
              ? tDynamic("Keys, lockbox & entry")
              : sectionMode === "proof"
                ? tDynamic("Proof of standards & condition")
                : tDynamic("Before you clean: entry walkthrough")}
          </CardTitle>
          <p className="text-xs text-muted-foreground pt-1">
            {sectionMode === "access"
              ? tDynamic("Use the host’s instructions, then show how you entered and upload key photos if required.")
              : sectionMode === "proof"
                ? tDynamic("Add required arrival proof, then start the visit timer when you are ready to clean.")
                : tDynamic("This step documents how the guest left the unit. Cleaning checklist “after” photos are for after you clean.")}
          </p>
        </CardHeader>
        )}
        <CardContent
          className={cn(
            "text-sm space-y-6",
            omitEmbeddedCardHeader && !turnoverV1 && !proofV1 ? "pt-6 sm:pt-6" : "",
            accessMinimalLayout && !turnoverV1 && !proofV1 && "max-w-md mx-auto",
            (turnoverV1 || proofV1) &&
              cn(
                "max-w-md mx-auto space-y-5 px-0 pt-0 text-[15px] font-[family-name:var(--pv-font)]",
                embedV1Footer ? CLEANER_MOBILE_EMBED_FOOTER_SCROLL_PAD : "pb-6"
              )
          )}
        >
          {(sectionMode === "all" || sectionMode === "access") && accessInstructions ? (
            <AccessInstructionsView instructions={accessInstructions} defaultOpen className="border-primary/20" />
          ) : null}

          {requiresOwnKeyConfirmation && sectionMode !== "proof" && (
            <div
              className={cn(
                "space-y-3 rounded-xl border p-4",
                turnoverV1 && "rounded-2xl px-4 py-4 shadow-[var(--pv-shadow-card)]"
              )}
              style={
                turnoverV1
                  ? { borderColor: "var(--pv-line)", background: "var(--pv-surface)" }
                  : undefined
              }
            >
              <div className="flex items-start gap-2">
                <KeyRound
                  className={cn("h-5 w-5 shrink-0 mt-0.5", turnoverV1 ? "" : "text-primary")}
                  style={turnoverV1 ? { color: "var(--pv-teal)" } : undefined}
                />
                <div className="space-y-1 min-w-0">
                  <p
                    className={cn("text-sm font-semibold", turnoverV1 && "text-[15px]")}
                    style={turnoverV1 ? { color: "var(--pv-ink)" } : undefined}
                  >
                    {t("ownKeyConfirmTitle")}
                  </p>
                  <p
                    className={cn("text-xs text-muted-foreground leading-relaxed", turnoverV1 && "text-[var(--pv-ink-2)]")}
                  >
                    {t("ownKeyConfirmHint")}
                  </p>
                </div>
              </div>
              {ownKeyConfirmed ? (
                <div
                  className={cn(
                    "flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400",
                    turnoverV1 && "rounded-xl px-3 py-2",
                    turnoverV1 && "bg-[var(--pv-teal-soft)]"
                  )}
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  {t("ownKeyConfirmedLabel")}
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <Button
                    type="button"
                    className={cn(
                      "w-full sm:w-auto",
                      turnoverV1 && "pv-tappable rounded-xl font-semibold text-white"
                    )}
                    style={turnoverV1 ? { background: "var(--pv-teal)" } : undefined}
                    onClick={() => setOwnKeyConfirmed(true)}
                  >
                    {t("ownKeyConfirmYes")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn("w-full sm:w-auto", turnoverV1 && "pv-tappable rounded-xl")}
                    style={turnoverV1 ? { borderColor: "var(--pv-line)", color: "var(--pv-ink-2)" } : undefined}
                    onClick={() =>
                      toast({
                        title: t("ownKeyConfirmNo"),
                        description: t("ownKeyConfirmNoHint"),
                        variant: "destructive",
                      })
                    }
                  >
                    {t("ownKeyConfirmNo")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {sectionMode !== "access" && !(omitEmbeddedCardHeader && sectionMode === "proof") && (
          <p className="text-muted-foreground">
            {useStandardsForCondition
              ? tDynamic(
                  "Do this before you start cleaning. Add photos or mark each item — your host sees them right away. When all required items show a green check, tap Continue."
                )
              : tDynamic(
                  "Before you start cleaning, take photos of how the guest left each room. Add at least one photo per room below."
                )}
          </p>
          )}

          {sectionMode !== "proof" && (
          <>
          {showKeysSection && (
            <div className={cn("space-y-6", accessMinimalLayout && !turnoverV1 && "text-center")}>
              {settings.notes?.trim() && accessMinimalLayout && !turnoverV1 ? (
                <p className="text-xs text-muted-foreground border border-border/80 rounded-lg p-3 bg-muted/25 text-left leading-relaxed">
                  {settings.notes}
                </p>
              ) : settings.notes?.trim() && turnoverV1 ? (
                <p
                  className="rounded-2xl border px-3 py-2 text-xs leading-relaxed"
                  style={{ borderColor: "var(--pv-line)", background: "var(--pv-surface-2)", color: "var(--pv-ink-2)" }}
                >
                  {settings.notes}
                </p>
              ) : null}

              {keyReferenceUrls.length > 0 ? (
                <div className={cn("space-y-3", turnoverV1 && "space-y-2")}>
                  {turnoverV1 ? (
                    <p className="text-[15px] font-semibold leading-tight" style={{ color: "var(--pv-ink)" }}>
                      {t("keyLocationReferenceSectionTitle")}{" "}
                      <span className="font-normal" style={{ color: "var(--pv-ink-2)" }}>
                        {t("keyLocationFromHostSuffix")}
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t("keyLocationReferenceTitle")}
                    </p>
                  )}
                  <div
                    className={cn(
                      "flex gap-2",
                      turnoverV1 ? "overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]" : "flex-wrap justify-center gap-3"
                    )}
                  >
                    {(turnoverV1 ? keyReferenceUrls.slice(0, 3) : keyReferenceUrls).map((url, idx) => {
                      const shells = ["#E6F4F2", "#EDE5D8", "#E2E8EE"];
                      const shell = shells[idx % shells.length];
                      return (
                        <button
                          key={url}
                          type="button"
                          className={cn(
                            "relative shrink-0 overflow-hidden rounded-2xl border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                            turnoverV1
                              ? "h-[104px] w-[104px] active:scale-[0.98] focus-visible:ring-[var(--pv-teal)]"
                              : "w-28 h-28 sm:w-32 sm:h-32 rounded-xl border bg-muted focus-visible:ring-primary"
                          )}
                          style={
                            turnoverV1
                              ? {
                                  borderColor: "var(--pv-line)",
                                  backgroundImage: `repeating-linear-gradient(135deg, ${shell} 0, ${shell} 8px, rgba(255,255,255,0.35) 8px, rgba(255,255,255,0.35) 16px)`,
                                }
                              : undefined
                          }
                          onClick={() => setKeyReferenceViewer({ index: idx })}
                          aria-label={t("keyLocationReferenceTitle")}
                        >
                          {turnoverV1 && (
                            <KeyRound
                              className="absolute left-2 top-2 h-4 w-4 opacity-70"
                              style={{ color: "var(--pv-ink)" }}
                              aria-hidden
                            />
                          )}
                          {isVideoUrl(url) ? (
                            <video src={url} className="h-full w-full object-cover" muted playsInline />
                          ) : (
                            <img src={url} alt="" className="h-full w-full object-cover mix-blend-multiply" />
                          )}
                        </button>
                      );
                    })}
                    {turnoverV1 && keyReferenceUrls.length > 3 ? (
                      <button
                        type="button"
                        onClick={() => setKeyReferenceViewer({ index: 3 })}
                        className="flex h-[104px] w-[104px] shrink-0 cursor-pointer items-center justify-center rounded-2xl border border-dashed text-xs font-semibold transition-colors hover:bg-muted/30"
                        style={{ borderColor: "var(--pv-line)", color: "var(--pv-ink-2)" }}
                      >
                        +{keyReferenceUrls.length - 3} {t("keyLocationMorePhotos")}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : turnoverV1 ? (
                <div className="space-y-2">
                  <p className="text-[15px] font-semibold leading-tight" style={{ color: "var(--pv-ink)" }}>
                    {t("keyLocationReferenceSectionTitle")}{" "}
                    <span className="font-normal" style={{ color: "var(--pv-ink-2)" }}>
                      {t("keyLocationFromHostSuffix")}
                    </span>
                  </p>
                  <div
                    className="flex flex-col items-center gap-2 rounded-2xl border border-dashed px-4 py-8 text-center text-sm"
                    style={{ borderColor: "var(--pv-line)", background: "var(--pv-surface)", color: "var(--pv-ink-2)" }}
                  >
                    <ImageIcon className="h-8 w-8 opacity-40" aria-hidden />
                    {t("keyLocationNoReference")}
                  </div>
                </div>
              ) : accessMinimalLayout ? (
                <p className="text-xs text-muted-foreground leading-relaxed">{t("keyLocationNoReference")}</p>
              ) : null}

              {showPrimaryKeyUpload ? (
                <div className={cn("space-y-2", accessMinimalLayout && !turnoverV1 ? "text-center" : "text-left max-w-xl")}>
                  {turnoverV1 ? (
                    <>
                      <input
                        ref={primaryFileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handlePrimaryKeyFiles}
                      />
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 flex-1 text-[15px] font-semibold leading-snug" style={{ color: "var(--pv-ink)" }}>
                          {t("keyLocationYourPhotoLabel")}
                        </p>
                        <span
                          className="shrink-0 text-[10px] font-bold uppercase tracking-wide"
                          style={{
                            color: primaryPhotoRequired ? "var(--pv-teal)" : "var(--pv-ink-3)",
                          }}
                        >
                          {primaryPhotoRequired ? t("keyLocationRequiredBadge") : t("keyLocationOptionalBadge")}
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--pv-ink-2)" }}>
                        {primaryPhotoRequired ? t("keyLocationYourPhotoHint") : t("keyLocationOptionalNoHostHint")}
                      </p>
                      {primaryKeyUploading ? (
                        <div
                          className="pv-shimmer flex h-[140px] items-center justify-center rounded-2xl border border-dashed"
                          style={{ borderColor: "var(--pv-teal)", background: "var(--pv-teal-soft)" }}
                        >
                          <span className="text-sm font-medium" style={{ color: "var(--pv-ink-2)" }}>
                            {t("keyLocationReferenceUploading")}
                          </span>
                        </div>
                      ) : primaryKeyProofUrls.length > 0 ? (
                        <div
                          className="pv-fadeup flex items-center gap-3 rounded-2xl border p-3"
                          style={{
                            borderColor: "var(--pv-line)",
                            background: "var(--pv-surface)",
                            boxShadow: "var(--pv-shadow-card)",
                          }}
                        >
                          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-muted">
                            <img
                              src={primaryKeyProofUrls[primaryKeyProofUrls.length - 1]}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                            <span className="absolute bottom-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--pv-teal)] text-white">
                              <CheckCircle2 className="h-3 w-3" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold" style={{ color: "var(--pv-ink)" }}>
                              {t("keyLocationPhotoReady")}
                            </p>
                            <p className="text-xs" style={{ color: "var(--pv-ink-2)" }}>
                              {primaryKeyProofUrls.length}{" "}
                              {primaryKeyProofUrls.length === 1 ? t("keyLocationPhotoCountOne") : t("keyLocationPhotoCountMany")}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="pv-tappable shrink-0 text-sm font-semibold"
                            style={{ color: "var(--pv-teal)" }}
                            onClick={() => {
                              setPrimaryKeyProofUrls([]);
                              setPrimaryKeyFiles([]);
                            }}
                          >
                            {t("keyLocationRetake")}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="pv-tappable flex min-h-[132px] w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-5"
                          style={{ borderColor: "var(--pv-teal)", background: "var(--pv-teal-soft)" }}
                          onClick={() => primaryFileInputRef.current?.click()}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className="flex h-12 w-12 items-center justify-center rounded-full shadow-sm"
                              style={{ background: "var(--pv-surface)" }}
                            >
                              <Camera className="h-6 w-6" style={{ color: "var(--pv-teal)" }} />
                            </span>
                            <div className="text-left">
                              <p className="text-[15px] font-semibold" style={{ color: "var(--pv-ink)" }}>
                                {t("keyLocationTakePhoto")}
                              </p>
                              <p className="text-xs" style={{ color: "var(--pv-ink-2)" }}>
                                {t("keyLocationUseCamera")}
                              </p>
                            </div>
                          </div>
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <Label className="text-sm font-medium">{t("keyLocationYourPhotoLabel")}</Label>
                      <p className="text-xs text-muted-foreground leading-relaxed">{t("keyLocationYourPhotoHint")}</p>
                      <div className={accessMinimalLayout ? "max-w-xs mx-auto" : ""}>
                        <input
                          id="entry-primary-key-file-input"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          multiple
                          className="hidden"
                          onChange={handlePrimaryKeyFiles}
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById("entry-primary-key-file-input")?.click()}
                          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-sm font-medium text-primary transition-colors hover:border-primary/60 hover:bg-primary/10"
                        >
                          <Camera className="h-4 w-4" />
                          {primaryKeyUploading
                            ? tDynamic("Uploading…")
                            : primaryKeyProofUrls.length > 0
                              ? t("keyLocationFilesSelected").replace(/\{\{count\}\}/g, String(primaryKeyProofUrls.length))
                              : t("keyLocationTakePhoto")}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {settings.backup_key_required && settings.backup_key_proof_required && (
            <div className={cn("space-y-2", accessMinimalLayout && !turnoverV1 && "text-center max-w-md mx-auto")}>
              {turnoverV1 ? (
                <>
                  <input
                    ref={backupFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleBackupKeyFiles}
                  />
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[15px] font-semibold" style={{ color: "var(--pv-ink)" }}>
                      {t("keyLocationBackupSectionTitle")}
                    </p>
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--pv-ink-3)" }}>
                      {t("keyLocationOptionalBadge")}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: "var(--pv-ink-2)" }}>
                    {t("keyLocationBackupHint")}
                  </p>
                  {backupKeyUploading ? (
                    <div
                      className="pv-shimmer flex h-[100px] items-center justify-center rounded-2xl border border-dashed"
                      style={{ borderColor: "var(--pv-line)", background: "var(--pv-surface-2)" }}
                    >
                      <span className="text-sm font-medium" style={{ color: "var(--pv-ink-2)" }}>
                        {t("keyLocationReferenceUploading")}
                      </span>
                    </div>
                  ) : backupKeyProofUrls.length > 0 ? (
                    <div
                      className="flex items-center justify-between rounded-2xl border px-3 py-2"
                      style={{ borderColor: "var(--pv-line)", background: "var(--pv-surface)" }}
                    >
                      <span className="text-sm font-medium" style={{ color: "var(--pv-ink)" }}>
                        {t("keyLocationPhotoReady")}
                      </span>
                      <button
                        type="button"
                        className="text-sm font-semibold"
                        style={{ color: "var(--pv-teal)" }}
                        onClick={() => {
                          setBackupKeyProofUrls([]);
                          setBackupFiles([]);
                        }}
                      >
                        {t("keyLocationRetake")}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="pv-tappable flex min-h-[100px] w-full items-center justify-center rounded-2xl border-2 border-dashed text-sm font-medium"
                      style={{ borderColor: "var(--pv-line)", background: "var(--pv-surface)" }}
                      onClick={() => backupFileInputRef.current?.click()}
                    >
                      {t("keyLocationTakePhoto")}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <Label>{tDynamic("Backup key — photo proof")}</Label>
                  <div className={accessMinimalLayout ? "max-w-xs mx-auto" : ""}>
                    <input
                      id="entry-backup-key-file-input"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleBackupKeyFiles}
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById("entry-backup-key-file-input")?.click()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-sm font-medium text-primary transition-colors hover:border-primary/60 hover:bg-primary/10"
                    >
                      <Camera className="h-4 w-4" />
                      {backupKeyUploading
                        ? "Uploading…"
                        : backupKeyProofUrls.length > 0
                          ? t("keyLocationFilesSelected").replace(/\{\{count\}\}/g, String(backupKeyProofUrls.length))
                          : t("keyLocationTakePhoto")}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className={cn("space-y-2", accessMinimalLayout && !turnoverV1 && "text-left max-w-md mx-auto w-full")}>
            <Label
              className={cn(
                "text-sm font-medium",
                accessMinimalLayout && !turnoverV1 && "block text-center",
                turnoverV1 && "text-[15px] font-semibold"
              )}
              style={turnoverV1 ? { color: "var(--pv-ink)" } : undefined}
            >
              {tDynamic("How did you enter?")}
            </Label>
            <Select
              value={turnoverV1 && !entryConfirmed ? undefined : entryType}
              onValueChange={(v) => {
                setEntryType(v as ChecklistEntryType);
                setEntryConfirmed(true);
              }}
            >
              <SelectTrigger
                className={cn(
                  "h-12 text-base",
                  turnoverV1 && "rounded-xl border bg-[var(--pv-surface)] font-normal shadow-[var(--pv-shadow-card)]",
                  accessMinimalLayout && !turnoverV1 && "h-12"
                )}
                style={turnoverV1 ? { borderColor: "var(--pv-line)", color: "var(--pv-ink)" } : undefined}
              >
                <SelectValue placeholder={t("keyLocationChooseEntry")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lockbox">Lockbox</SelectItem>
                <SelectItem value="smart_lock">Smart lock</SelectItem>
                <SelectItem value="key_handoff">Key handoff</SelectItem>
                <SelectItem value="other">{tDynamic("Other")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          </>
          )}

          {sectionMode !== "access" && useStandardsForCondition && (
            <>
              {proofV1 ? (
                <UnitCheckRoomWalkthrough
                  standards={proofStandardsList}
                  checklistRoomNames={roomNames}
                  proofMediaUrlsByStandardId={proofMediaUrlsByStandardId}
                  functionalPassByKey={functionalPassByStandardId}
                  onToggleFunctionalPass={(standardId, roomName, nextPassed) => {
                    const key = unitCheckProofKey(standardId, roomName);
                    setFunctionalPassByStandardId((prev) => ({ ...prev, [key]: nextPassed }));
                  }}
                  proofUploadingStandardId={proofUploadingStandardId}
                  submitting={submitting}
                  extraDirty={extraDirty}
                  onOpenReference={setProofReferenceModal}
                  onPickFiles={(standardId, roomName, files) => {
                    void handleProofFilesChosen(standardId, files, roomName);
                  }}
                  onRemoveProofUrl={removeProofUrl}
                  isVideoUrl={isVideoUrl}
                  t={t}
                  tDynamic={tDynamic}
                  propertyId={propertyId}
                  cleaningTaskId={taskId}
                  cleanerId={cleanerId}
                  checklistName={checklist?.name ?? null}
                />
              ) : (
                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 font-medium">
                    <Camera className="h-4 w-4 shrink-0" />
                    {t("preVisitBeforePhotosTitle")}
                  </h3>
                  {(() => {
                    const sections = buildUnitCheckWalkthrough(
                      proofStandardsList,
                      roomNames,
                      tDynamic("General"),
                    );
                    return (
                      <div className="space-y-5">
                        {sections.map(({ label, items }) => (
                          <div key={label}>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {label}
                            </p>
                            <ul className="space-y-2">
                              {items.map((s) => {
                                const proofKey = unitCheckProofKey(s.id, label);
                                const urls = proofUrlsForUnitCheck(
                                  proofMediaUrlsByStandardId,
                                  s.id,
                                  label,
                                );
                                const passedFunctional =
                                  s.type === "functional_check" &&
                                  !!functionalPassByStandardId[proofKey];
                                const hasProof = urls.length > 0;
                                const satisfied = isUnitCheckSatisfied(
                                  s,
                                  label,
                                  proofMediaUrlsByStandardId,
                                  functionalPassByStandardId,
                                );
                                const isRequired = s.required_every_turnover;
                                const isUploadingThis = proofUploadingStandardId === proofKey;
                                const accept =
                                  s.type === "proof_video" ? "video/*,image/*" : "image/*,video/*";
                                const fileInputId = `entry-proof-${proofKey}`;
                                return (
                                  <li key={proofKey} className="space-y-3 rounded border p-3 text-sm">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                      <div className="min-w-0 flex-1">
                                        <span className="font-medium">{s.title}</span>
                                        <div className="mt-1.5">
                                          <UnitItemProofTypeBadge type={s.type} t={t} />
                                        </div>
                                        <div className="mt-1 flex items-center gap-2">
                                          <span
                                            className={`inline-flex items-center gap-1 text-xs ${
                                              satisfied ? "text-green-600" : isRequired ? "text-destructive" : "text-muted-foreground"
                                            }`}
                                          >
                                            {satisfied ? (
                                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                                            ) : (
                                              <XCircle className="h-3.5 w-3.5 shrink-0" />
                                            )}
                                            {passedFunctional && !hasProof
                                              ? tDynamic("Marked pass")
                                              : hasProof
                                                ? `${urls.length} ${tDynamic("file(s) uploaded")}`
                                                : isRequired
                                                  ? tDynamic("Required")
                                                  : tDynamic("Optional")}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex shrink-0 flex-wrap justify-end gap-2">
                                        {s.type === "functional_check" ? (
                                          <Button
                                            type="button"
                                            variant={passedFunctional ? "secondary" : "outline"}
                                            size="sm"
                                            disabled={submitting || hasProof}
                                            onClick={() =>
                                              setFunctionalPassByStandardId((prev) => ({
                                                ...prev,
                                                [proofKey]: !prev[proofKey],
                                              }))
                                            }
                                          >
                                            {passedFunctional ? tDynamic("Undo pass") : tDynamic("Mark pass")}
                                          </Button>
                                        ) : null}
                                        <Button type="button" variant="outline" size="sm" onClick={() => setProofReferenceModal(s)}>
                                          {t("unitItemsHostNotesLink")}
                                        </Button>
                                        <input
                                          id={fileInputId}
                                          type="file"
                                          accept={accept}
                                          multiple
                                          className="hidden"
                                          onChange={(e) => {
                                            void handleProofFilesChosen(s.id, e.target.files, label);
                                            e.target.value = "";
                                          }}
                                        />
                                        <Button
                                          type="button"
                                          variant={hasProof ? "secondary" : "default"}
                                          size="sm"
                                          disabled={submitting || isUploadingThis}
                                          onClick={() => document.getElementById(fileInputId)?.click()}
                                        >
                                          {isUploadingThis ? (
                                            <Loader2 className="mr-1 h-4 w-4 shrink-0 animate-spin" aria-hidden />
                                          ) : s.type === "proof_video" ? (
                                            <Video className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                                          ) : s.type === "functional_check" ? (
                                            <ClipboardCheck className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                                          ) : (
                                            <Camera className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                                          )}
                                          {isUploadingThis
                                            ? tDynamic("Uploading…")
                                            : hasProof
                                              ? tDynamic("Add more")
                                              : tDynamic("Add proof")}
                                        </Button>
                                      </div>
                                    </div>
                                    {urls.length > 0 && (
                                      <div className="flex flex-wrap gap-2">
                                        {urls.map((url) => (
                                          <div key={url} className="relative h-20 w-20 shrink-0 overflow-hidden rounded border bg-muted">
                                            {isVideoUrl(url) ? (
                                              <video src={url} className="h-full w-full object-cover" muted playsInline />
                                            ) : (
                                              <img src={url} alt="" className="h-full w-full object-cover" />
                                            )}
                                            <button
                                              type="button"
                                              className="absolute right-0.5 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border bg-background/90 opacity-90 shadow hover:opacity-100"
                                              onClick={() => removeProofUrl(s.id, url, label)}
                                              aria-label={tDynamic("Remove")}
                                            >
                                              <X className="h-3.5 w-3.5" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <UnitCheckIssueReport
                                      standard={s}
                                      propertyId={propertyId}
                                      cleaningTaskId={taskId}
                                      cleanerId={cleanerId}
                                      checklistName={checklist?.name ?? null}
                                      roomNameOverride={label}
                                      tDynamic={tDynamic}
                                      disabled={submitting}
                                    />
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}

          {sectionMode !== "access" && sectionMode !== "proof" && !useStandardsForCondition && roomNames.length > 0 && (
            <div className={cn("space-y-4", proofV1 && "space-y-3")}>
              {useStandardsForCondition ? (
                <p className="text-xs text-muted-foreground" style={proofV1 ? { color: "var(--pv-ink-2)" } : undefined}>
                  {t("preVisitOptionalRoomPhotosHint")}
                </p>
              ) : null}
              <h3
                className={cn("flex items-center gap-2 font-medium", proofV1 && "text-[15px] font-semibold")}
                style={proofV1 ? { color: "var(--pv-ink)" } : undefined}
              >
                <Camera className="h-4 w-4 shrink-0" style={proofV1 ? { color: "var(--pv-teal)" } : undefined} />
                {tDynamic("Before-cleaning photos — take these BEFORE you start")}
              </h3>
              {roomNames.map((room, roomIdx) => {
                const inputId = `entry-condition-photo-${roomIdx}`;
                const slots = conditionByRoom[room] ?? [];
                return (
                  <div
                    key={room}
                    className={cn("space-y-2 rounded-lg border p-3", proofV1 && "rounded-[var(--pv-r-lg)] shadow-[var(--pv-shadow-card)]")}
                    style={proofV1 ? { borderColor: "var(--pv-line)", background: "var(--pv-surface)" } : undefined}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <Label
                        className="min-w-0 flex-1 break-words pr-1 pt-0.5 text-left leading-snug"
                        style={proofV1 ? { color: "var(--pv-ink)" } : undefined}
                      >
                        {room}
                      </Label>
                      <div className="flex shrink-0 flex-col items-end">
                        <input
                          id={inputId}
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            addConditionPhotos(room, e.target.files);
                            e.target.value = "";
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById(inputId)?.click()}
                          className={cn(
                            "whitespace-nowrap",
                            proofV1 && "border-[var(--pv-line)] bg-[var(--pv-surface-2)] text-[var(--pv-ink)]"
                          )}
                        >
                          <Plus className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                          {slots.length > 0 ? tDynamic("Add more photos") : tDynamic("Add photos")}
                        </Button>
                      </div>
                    </div>
                    {slots.length > 0 ? (
                      <>
                        <div className="flex flex-wrap gap-2">
                          {slots.map((slot, i) => (
                            <div
                              key={`${slot.previewUrl}-${i}`}
                              className={cn(
                                "relative h-20 w-20 shrink-0 overflow-hidden rounded-md border bg-muted",
                                proofV1 && "border-[var(--pv-line)]"
                              )}
                            >
                              {slot.file.type.startsWith("video/") ? (
                                <video
                                  src={slot.previewUrl}
                                  className="h-full w-full object-cover"
                                  muted
                                  playsInline
                                />
                              ) : (
                                <img src={slot.previewUrl} alt="" className="h-full w-full object-cover" />
                              )}
                              <button
                                type="button"
                                className={cn(
                                  "absolute right-0.5 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border bg-background/90 opacity-90 shadow hover:opacity-100",
                                  proofV1 && "border-[var(--pv-line)]"
                                )}
                                onClick={() => removeConditionPhoto(room, i)}
                                aria-label={tDynamic("Remove")}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <p
                          className="text-xs text-muted-foreground"
                          style={proofV1 ? { color: "var(--pv-ink-2)" } : undefined}
                        >
                          {slots.length} {tDynamic("photo(s)")}
                        </p>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {sectionMode !== "access" && unitItems.length > 0 && (
            <div
              className={cn("rounded-lg border border-muted p-3", proofV1 && "rounded-[var(--pv-r-lg)] shadow-[var(--pv-shadow-card)]")}
              style={proofV1 ? { borderColor: "var(--pv-line)", background: "var(--pv-surface-2)" } : undefined}
            >
              <p className="mb-1 font-medium" style={proofV1 ? { color: "var(--pv-ink)" } : undefined}>
                {t("cleanerEntryUnitScopedInventoryTitle")}
              </p>
              <ul
                className={cn("list-disc space-y-1 pl-5 text-xs text-muted-foreground", proofV1 && "text-[var(--pv-ink-2)]")}
              >
                {unitItems.map((it: { id: string; item_name: string }) => (
                  <li key={it.id}>{it.item_name}</li>
                ))}
              </ul>
            </div>
          )}

          {sectionMode === "all" && (
            <div
              className={cn(
                "flex flex-col gap-2",
                proofV1
                  ? "rounded-[var(--pv-r-lg)] border p-4 shadow-[var(--pv-shadow-card)]"
                  : "border-t pt-4"
              )}
              style={proofV1 ? { borderColor: "var(--pv-line)", background: "var(--pv-surface-2)" } : undefined}
            >
              <Button
                type="button"
                variant="outline"
                onClick={() => setIncidentOpen((v) => !v)}
                className={cn(
                  "w-full",
                  proofV1 &&
                    "h-11 rounded-xl border-[var(--pv-line)] bg-[var(--pv-surface)] font-medium text-[var(--pv-ink)] shadow-[var(--pv-shadow-card)] pv-tappable"
                )}
              >
                <AlertTriangle className={cn("mr-2 h-4 w-4", proofV1 && "text-[var(--pv-warn)]")} />
                {tDynamic("Report incident to owner")}
              </Button>
              {incidentOpen && (
                <div
                  className={cn("space-y-2 rounded-lg border p-3", proofV1 ? "border-[var(--pv-line)] bg-[var(--pv-surface)]" : "bg-muted/30")}
                >
                  <Input
                    placeholder={tDynamic("Short title")}
                    value={incidentTitle}
                    onChange={(e) => setIncidentTitle(e.target.value)}
                  />
                  <Textarea
                    placeholder={tDynamic("Describe what you found — include photo if possible")}
                    value={incidentDesc}
                    onChange={(e) => setIncidentDesc(e.target.value)}
                    rows={3}
                  />
                  <div>
                    <input
                      id="entry-incident-file-input"
                      type="file"
                      accept="image/*,video/*"
                      className="hidden"
                      onChange={(e) => setIncidentFile(e.target.files?.[0] ?? null)}
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById("entry-incident-file-input")?.click()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-sm font-medium text-primary transition-colors hover:border-primary/60 hover:bg-primary/10"
                    >
                      <Camera className="h-4 w-4" />
                      {incidentFile ? incidentFile.name : tDynamic("Add photo or video")}
                    </button>
                  </div>
                  <Button type="button" size="sm" onClick={handleSubmitIncident}>
                    {tDynamic("Send report")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {proofV1 && proofStickyFooter ? (
            <div className="flex items-start gap-2 px-0.5 pb-1 pt-1">
              <Shield className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--pv-teal)" }} aria-hidden />
              <p className="text-xs leading-relaxed" style={{ color: "var(--pv-ink-2)" }}>
                {t("preVisitProofPrivacyFootnote")}
              </p>
            </div>
          ) : null}

          {sectionMode === "access" ? (
            turnoverV1 ? (
              <div
                className={cn(
                  "fixed bottom-0 left-0 right-0 z-[55] border-t px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 md:z-30",
                  CLEANER_MOBILE_BOTTOM_NAV_OFFSET
                )}
                style={{
                  background: "var(--pv-surface)",
                  boxShadow: "var(--pv-shadow-footer)",
                  borderColor: "var(--pv-line)",
                }}
              >
                <div className="mx-auto w-full max-w-md">
                  {showV1PhotoValidation ? (
                    <p
                      className="mb-2 flex items-center justify-center gap-1.5 text-center text-xs font-medium"
                      style={{ color: "var(--pv-warn)" }}
                    >
                      <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {t("preVisitAddPhotoToContinue")}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    className={cn(
                      "h-12 w-full rounded-full text-base font-semibold text-white shadow-sm transition-colors",
                      accessContinueDisabled && "pointer-events-none opacity-50"
                    )}
                    style={{
                      backgroundColor: accessContinueDisabled ? "var(--pv-line)" : "var(--pv-teal)",
                    }}
                    onClick={handleSaveAccessOnly}
                    disabled={accessContinueDisabled}
                  >
                    {submitting ? tDynamic("Saving…") : t("continue")}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className={cn("w-full h-12", accessMinimalLayout && "max-w-md mx-auto")}
                onClick={handleSaveAccessOnly}
                disabled={accessContinueDisabled}
              >
                {submitting ? tDynamic("Saving…") : t("continue")}
              </Button>
            )
          ) : proofStickyFooter ? (
            <div
              className={cn(
                "fixed bottom-0 left-0 right-0 z-[55] border-t px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 md:z-30",
                CLEANER_MOBILE_BOTTOM_NAV_OFFSET
              )}
              style={{
                background: "var(--pv-surface)",
                boxShadow: "var(--pv-shadow-footer)",
                borderColor: "var(--pv-line)",
              }}
            >
              <div className="mx-auto w-full max-w-md">
                {showProofV1ValidationHint ? (
                  <p
                    className="mb-2 flex items-center justify-center gap-1.5 text-center text-xs font-medium"
                    style={{ color: "var(--pv-warn)" }}
                  >
                    <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {t("preVisitProofMissingHint")}
                  </p>
                ) : null}
                <Button
                  type="button"
                  className={cn(
                    "h-12 w-full rounded-full text-base font-semibold text-white shadow-sm transition-colors",
                    proofContinueDisabled && "pointer-events-none opacity-50"
                  )}
                  style={{
                    backgroundColor: proofContinueDisabled ? "var(--pv-line)" : "var(--pv-teal)",
                  }}
                  onClick={handleComplete}
                  disabled={proofContinueDisabled}
                >
                  {submitting ? tDynamic("Saving…") : t("preVisitProofPrimaryCta")}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              className="h-12 w-full"
              onClick={handleComplete}
              disabled={submitting || proofUploadingStandardId !== null}
            >
              {submitting
                ? tDynamic("Saving…")
                : onStartVisitTimer
                  ? t("preVisitProofPrimaryCta")
                  : tDynamic("Continue to cleaning checklist")}
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!proofReferenceModal} onOpenChange={() => setProofReferenceModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{proofReferenceModal?.title}</DialogTitle>
            <DialogDescription className="sr-only">{t("unitItemsModalSrOnly")}</DialogDescription>
          </DialogHeader>
          {proofReferenceModal ? (
            <ProofStandardHostDetailContent standard={proofReferenceModal} t={t} isVideoUrl={isVideoUrl} />
          ) : null}
        </DialogContent>
      </Dialog>

      <CleanerMediaLightbox
        open={keyReferenceViewer !== null}
        onOpenChange={(next) => {
          if (!next) setKeyReferenceViewer(null);
        }}
        urls={keyReferenceUrls}
        initialIndex={keyReferenceViewer?.index ?? 0}
      />
    </div>
  );
}
